import { useRef, useState, useEffect, useCallback } from "react";
import { Swarm } from "@lbs/swarm-client";

import { ISelection } from "src/constants/interfaces/bet";
import { getGameMarketEventByIdSwarmQuery } from "src/common/swarm/queries/events";
import { ISwarmGameResponse, SwarmResponse } from "src/common/swarm/models/response";
import { storeChangeSelection } from "src/stores/selections";
import { getEventsFromGame } from "src/common/swarm/destructure";
import _ from "lodash";

export const useSelectionSubscription = (selection: ISelection) => {
  const { id: gameId } = selection.game;
  const { id: marketId } = selection.market;
  const { id: eventId } = selection.event;

  const loadingTransitionTimer = useRef<NodeJS.Timer | null>();
  const [loading, setLoading] = useState(false);

  const selectionRef = useRef<ISelection>(selection);

  const updateSelection = useCallback(
    ({ game: gameData }: { game?: SwarmResponse<ISwarmGameResponse> }) => {
      if (!gameData) return storeChangeSelection(eventId, { ...selectionRef.current, loaded: false });

      loadingTransitionTimer.current && clearTimeout(loadingTransitionTimer.current);
      setLoading(false);

      if (_.isEmpty(gameData) || !gameData?.[`${gameId}`]) {
        return storeChangeSelection(eventId, { ...selectionRef.current, inactive: true, loaded: true });
      }

      const [{ event, market, game }] = getEventsFromGame(gameData);

      storeChangeSelection(eventId, {
        ...selectionRef.current,
        inactive: false,
        game: { ...selectionRef.current.game, ...game },
        event: { ...selectionRef.current.event, ...event },
        market: { ...selectionRef.current.market, ...market },
        loaded: true,
      });
    },
    [eventId]
  );

  useEffect(() => {
    let subId: string;

    (async () => {
      loadingTransitionTimer.current = setTimeout(() => {
        setLoading(true);
      }, 350);

      const { subid, data } = await Swarm.getInstance().subscribe(
        getGameMarketEventByIdSwarmQuery(gameId, marketId, eventId),
        updateSelection,
        true
      );

      updateSelection(data);

      subId = subid;
    })();

    return () => {
      loadingTransitionTimer.current && clearTimeout(loadingTransitionTimer.current);
      subId && Swarm.getInstance().unsubscribe(subId);
    };
  }, [gameId, marketId, eventId, updateSelection]);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  return loading;
};
